<script setup lang="ts">
import { cloneDeep } from 'lodash-es';
import {
  computed,
  ref,
} from 'vue';

import type {
  IFormSelectInputOption,
  ISmartFilterCompareRowData,
} from '@modules/forms/types';
import type { ISmartFilterColumnDataCompare } from '@modules/smart-filters/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VFormRadioInput from '@modules/forms/components/inputs/VFormRadioInput/VFormRadioInput.vue';
import { useI18n } from '@modules/i18n/composables';
import VSmartFilterCompareRow from '@modules/smart-filters/components/VSmartFilterCompareRow/VSmartFilterCompareRow.vue';
import {
  DEFAULT_SMART_FILTER_COMPARE_DATA,
  TRANSLATION_GROUP_SMART_FILTERS,
} from '@modules/smart-filters/constants';
import {
  ESmartFilterCompareInputsPath,
  ESmartFilterCompareMarks,
  ESmartFilterCompareSeparators,
} from '@modules/smart-filters/enums';
import { QA_TABLES } from '@modules/tables/constants';
import { watchImmediate } from '@vueuse/core';

import type {
  ISmartFilterCompareEmits,
  ISmartFilterCompareProps,
} from './types';

const props = defineProps<ISmartFilterCompareProps>();
const emit = defineEmits<ISmartFilterCompareEmits>();

const { getTranslation } = useI18n();

const marksList = computed<IFormSelectInputOption<ESmartFilterCompareMarks>[]>(() => [
  {
    value: ESmartFilterCompareMarks.LESS_THAN,
    label: getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_LESS_THAN').value,
  },
  {
    value: ESmartFilterCompareMarks.LESS_THAN_OR_EQUAL,
    label: getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_LESS_THAN_OR_EQUAL').value,
  },
  {
    value: ESmartFilterCompareMarks.GREATER_THAN,
    label: getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_GREATER_THAN').value,
  },
  {
    value: ESmartFilterCompareMarks.GREATER_THAN_OR_EQUAL,
    label: getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_GREATER_THAN_OR_EQUAL').value,
  },
  {
    value: ESmartFilterCompareMarks.EQUAL,
    label: getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_EQUAL').value,
  },
]);

const radioOptions = computed<IFormSelectInputOption<ESmartFilterCompareSeparators>[]>(() => [
  {
    value: ESmartFilterCompareSeparators.AND,
    label: getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_AND').value,
  },
  {
    value: ESmartFilterCompareSeparators.OR,
    label: getTranslation(TRANSLATION_GROUP_SMART_FILTERS, 'SEGMENTS_COMPARE_OR').value,
  },
]);

const innerValue = ref<ISmartFilterColumnDataCompare>(cloneDeep(DEFAULT_SMART_FILTER_COMPARE_DATA));
const showSecondCondition = computed(() => !!innerValue.value.compare_separator);

function onChangeRow(rowData: ISmartFilterCompareRowData, index: number): void {
  let data: ISmartFilterColumnDataCompare;

  switch (index) {
    case 1:
      data = {
        ...innerValue.value,
        first_condition: {
          ...innerValue.value.first_condition,
          first: rowData,
        },
      };
      break;
    case 2:
      data = {
        ...innerValue.value,
        first_condition: {
          ...innerValue.value.first_condition,
          second: rowData,
        },
      };
      break;
    case 3:
      data = {
        ...innerValue.value,
        second_condition: {
          ...innerValue.value.second_condition,
          first: rowData,
        },
      };
      break;
    case 4:
      data = {
        ...innerValue.value,
        second_condition: {
          ...innerValue.value.second_condition,
          second: rowData,
        },
      };
      break;
    default:
      throw new Error('Unknown index');
  }

  innerValue.value = data;
  emitDataChange();
}

function emitDataChange(): void {
  emit('change', innerValue.value);
}

function onClearRow(index: number): void {
  onChangeRow({ mark: null, value: null }, index);
}

function onSeparatorChange(value?: ESmartFilterCompareSeparators): void {
  innerValue.value.compare_separator = value || null;
  emitDataChange();
}

function toggleSecondCondition(): void {
  if (showSecondCondition.value) {
    innerValue.value.second_condition = cloneDeep(DEFAULT_SMART_FILTER_COMPARE_DATA.second_condition);
  }
  onSeparatorChange(showSecondCondition.value ? undefined : ESmartFilterCompareSeparators.AND);
}

watchImmediate(() => props.data, (newValue) => {
  innerValue.value = cloneDeep(newValue ?? DEFAULT_SMART_FILTER_COMPARE_DATA);
}, { deep: true });
</script>

<template>
  <div v-data-qa-auto-id="'VSmartFilterCompare'" v-data-test="{ el: QA_TABLES.filterCompareRoot }">
    <VSmartFilterCompareRow
      v-data-test="{ el: QA_TABLES.filterCompareRow, rowIndex: 0 }"
      :options="marksList"
      :mark-name="ESmartFilterCompareInputsPath.FIRST_CONDITION_FIRST_MARK"
      :mark-value="innerValue?.first_condition?.first?.mark || undefined"
      :number-name="ESmartFilterCompareInputsPath.FIRST_CONDITION_FIRST_VALUE"
      :number-value="innerValue?.first_condition?.first?.value ?? undefined"
      :disable-clear-button="!innerValue?.first_condition?.first?.mark
        && !innerValue?.first_condition?.first?.value"
      :is-supervision="isSupervision"
      @change="onChangeRow($event, 1)"
      @clear="onClearRow(1)"
    />

    <PDivider class="mt-1 block p-1 sm:hidden" />

    <VSmartFilterCompareRow
      v-data-test="{ el: QA_TABLES.filterCompareRow, rowIndex: 1 }"
      :options="marksList"
      :mark-name="ESmartFilterCompareInputsPath.FIRST_CONDITION_SECOND_MARK"
      :mark-value="innerValue?.first_condition?.second?.mark || undefined"
      :number-name="ESmartFilterCompareInputsPath.FIRST_CONDITION_SECOND_VALUE"
      :number-value="innerValue?.first_condition?.second?.value ?? undefined"
      :disable-clear-button="!innerValue?.first_condition?.second?.mark
        && !innerValue?.first_condition?.second?.value"
      :is-supervision="isSupervision"
      @change="onChangeRow($event, 2)"
      @clear="onClearRow(2)"
    />

    <div v-if="showSecondCondition">
      <VFormRadioInput
        :options="radioOptions"
        :name="ESmartFilterCompareInputsPath.SEPARATOR"
        :value="innerValue.compare_separator || undefined"
        :disabled="isSupervision"
        @change="onSeparatorChange"
      />

      <VSmartFilterCompareRow
        v-data-test="{ el: QA_TABLES.filterCompareRow, rowIndex: 2 }"
        :options="marksList"
        :mark-name="ESmartFilterCompareInputsPath.SECOND_CONDITION_FIRST_MARK"
        :mark-value="innerValue?.second_condition?.first?.mark || undefined"
        :number-name="ESmartFilterCompareInputsPath.SECOND_CONDITION_FIRST_VALUE"
        :number-value="innerValue?.second_condition?.first?.value ?? undefined"
        :disable-clear-button="!innerValue?.second_condition?.first?.mark
          && !innerValue?.second_condition?.first?.value"
        :is-supervision="isSupervision"
        @change="onChangeRow($event, 3)"
        @clear="onClearRow(3)"
      />

      <PDivider class="mt-1 block p-1 sm:hidden" />

      <VSmartFilterCompareRow
        v-data-test="{ el: QA_TABLES.filterCompareRow, rowIndex: 3 }"
        :options="marksList"
        :mark-name="ESmartFilterCompareInputsPath.SECOND_CONDITION_SECOND_MARK"
        :mark-value="innerValue?.second_condition?.second?.mark || undefined"
        :number-name="ESmartFilterCompareInputsPath.SECOND_CONDITION_SECOND_VALUE"
        :number-value="innerValue?.second_condition?.second?.value ?? undefined"
        :disable-clear-button="!innerValue?.second_condition?.second?.mark
          && !innerValue?.second_condition?.second?.value"
        :is-supervision="isSupervision"
        @change="onChangeRow($event, 4)"
        @clear="onClearRow(4)"
      />
    </div>
    <div>
      <VButton
        v-data-test="{ el: QA_TABLES.filterCompareAddOrRemove }"
        :label="$getTranslation(
          TRANSLATION_GROUP_SMART_FILTERS,
          showSecondCondition ? 'SEGMENTS_COMPARE_REMOVE_CONDITION' : 'SEGMENTS_COMPARE_ADD_CONDITION',
        )"
        :icon="`pi pi-${showSecondCondition ? 'minus' : 'plus'}`"
        :disabled="isSupervision"
        text
        @click="toggleSecondCondition"
      />
    </div>
  </div>
</template>
